import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { demandProfileService } from "../../utils/services/demandProfileService";

const DemandProfileModal = ({ open, onClose, onSuccess, editData }) => {
  const isEdit = !!editData;

  const initialData = {
    item: "",
    q1_units: "",
    q2_units: "",
    q3_units: "",
    q4_units: "",
  };

  const [formData, setFormData] = useState(initialData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        item: editData.item || "",
        q1_units: editData.q1_units ?? "",
        q2_units: editData.q2_units ?? "",
        q3_units: editData.q3_units ?? "",
        q4_units: editData.q4_units ?? "",
      });
    } else {
      setFormData(initialData);
    }
  }, [editData, open]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.item.trim()) {
      toast.error("Item is required");
      return;
    }
    for (let q of ["q1_units", "q2_units", "q3_units", "q4_units"]) {
      if (formData[q] === "" || isNaN(Number(formData[q]))) {
        toast.error(`All quarter units are required and must be numbers`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        item: formData.item,
        q1_units: Number(formData.q1_units),
        q2_units: Number(formData.q2_units),
        q3_units: Number(formData.q3_units),
        q4_units: Number(formData.q4_units),
      };
      if (isEdit) {
        await demandProfileService.edit(payload, editData._id);
        toast.success("Demand profile updated successfully!");
      } else {
        await demandProfileService.create(payload);
        toast.success("Demand profile created successfully!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Submit failed:", error);
      toast.error("Failed to save demand profile");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Demand Profile" : "Create Demand Profile"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            fullWidth
            label="Item"
            value={formData.item}
            onChange={(e) => handleInputChange("item", e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Q1 Units"
            type="number"
            value={formData.q1_units}
            onChange={(e) => handleInputChange("q1_units", e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Q2 Units"
            type="number"
            value={formData.q2_units}
            onChange={(e) => handleInputChange("q2_units", e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Q3 Units"
            type="number"
            value={formData.q3_units}
            onChange={(e) => handleInputChange("q3_units", e.target.value)}
            required
          />
          <TextField
            fullWidth
            label="Q4 Units"
            type="number"
            value={formData.q4_units}
            onChange={(e) => handleInputChange("q4_units", e.target.value)}
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={20} />
          ) : isEdit ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemandProfileModal;
